import Badge from "@intility/bifrost-react/Badge";
import Drawer from "@intility/bifrost-react/Drawer";
import Message from "@intility/bifrost-react/Message";
import Section from "@intility/bifrost-react/Section";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  createResourceType,
  createUsage,
  deleteUsage,
  getBlueprints,
  listResourceTypes,
  updateUsage,
  validateUsagePlacement,
} from "~/api/client";
import type {
  BlueprintNode,
  BlueprintResponse,
  CreateResourceTypeRequest,
  ResourceType,
} from "~/api/fga-types";
import { usePlatformNames } from "~/api/platforms";
import { PageLayout } from "../../components/layout/PageLayout";
import BlueprintCanvas, { type BlueprintSelection } from "./BlueprintCanvas";
import BlueprintDrawer from "./BlueprintDrawer";

function findNode(
  nodes: BlueprintNode[],
  usageId: string,
): BlueprintNode | null {
  for (const n of nodes) {
    if (n.usageId === usageId) return n;
    const found = findNode(n.children, usageId);
    if (found) return found;
  }
  return null;
}

export default function BlueprintViewer() {
  const [blueprint, setBlueprint] = useState<BlueprintResponse | null>(null);
  const platforms = usePlatformNames();
  const [types, setTypes] = useState<ResourceType[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selection, setSelection] = useState<BlueprintSelection | null>(null);

  const loadAll = useCallback(async () => {
    setError(null);
    try {
      const [bp, ts] = await Promise.all([
        getBlueprints(),
        listResourceTypes(),
      ]);
      setBlueprint(bp);
      setTypes(ts.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleSelect = (id: string | null, sel: BlueprintSelection | null) => {
    setSelectedId(id);
    setSelection(sel);
  };

  const closeDrawer = () => {
    setSelectedId(null);
    setSelection(null);
  };

  const siblingTypeIdsFor = useCallback(
    (platformSlug: string, parentUsageId: string | null): Set<string> => {
      const p = blueprint?.platforms.find((pf) => pf.platform === platformSlug);
      if (!p) return new Set();
      const siblings =
        parentUsageId === null
          ? p.tree
          : (findNode(p.tree, parentUsageId)?.children ?? []);
      return new Set(siblings.map((n) => n.type.id));
    },
    [blueprint],
  );

  const availableTypes = useMemo<ResourceType[]>(() => {
    if (!selection) return [];
    if (selection.kind === "platform") {
      const siblings = siblingTypeIdsFor(selection.platformSlug, null);
      return types.filter((t) => !siblings.has(t.id));
    }
    if (selection.kind === "resource") {
      const siblings = siblingTypeIdsFor(
        selection.platformSlug,
        selection.usageId,
      );
      return types.filter((t) => !siblings.has(t.id));
    }
    return [];
  }, [selection, siblingTypeIdsFor, types]);

  const handleAddUsage = async (
    platformSlug: string,
    parentUsageId: string | null,
    typeId: string,
    qualifiedName?: string,
  ) => {
    setBusy(true);
    setError(null);
    try {
      const type = availableTypes.find((t) => t.id === typeId);
      await createUsage(platformSlug, {
        typeId,
        parentUsageId: parentUsageId ?? undefined,
        qualifiedName: qualifiedName || type?.slug || typeId,
      });
      await loadAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const handleUpdateUsage = async (usageId: string, qualifiedName: string) => {
    setBusy(true);
    setError(null);
    try {
      await updateUsage(usageId, { qualifiedName });
      await loadAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteUsage = async (usageId: string) => {
    setBusy(true);
    setError(null);
    try {
      await deleteUsage(usageId);
      closeDrawer();
      await loadAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const handleCreateType = async (
    req: CreateResourceTypeRequest,
  ): Promise<ResourceType | undefined> => {
    setBusy(true);
    setError(null);
    try {
      const newType = await createResourceType(req);
      await loadAll();
      return newType;
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  // Prechecks a placement for a not-yet-created type before it's created, so a
  // placement that would be rejected never leaves an orphan type behind.
  const handleValidatePlacement = async (
    platformSlug: string,
    parentUsageId: string | null,
    qualifiedName: string,
    typeNameRegex: string | undefined,
    typeIsStatic: boolean,
  ) => {
    setBusy(true);
    setError(null);
    try {
      await validateUsagePlacement(platformSlug, {
        parentUsageId,
        qualifiedName,
        typeNameRegex,
        typeIsStatic,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      throw err;
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageLayout
      breadcrumbs={[
        { label: "Home", to: "/" },
        { label: "Authorization" },
        { label: "Blueprints" },
        { label: "Viewer" },
      ]}
      title="Blueprint"
      description={
        <>
          Structural tree of how a company's platforms are composed. Each node
          is a resource <em>type</em> placement; the same type can appear at
          multiple positions, but not twice under the same parent. Click a node
          to add or remove children.
        </>
      }
      fullWidthBody
    >
      {error && (
        <Message state="alert" header="Error">
          {error}
        </Message>
      )}

      {blueprint && (
        <Section>
          <Section.Header>
            <Badge state="neutral">
              {blueprint.platforms.length} platform
              {blueprint.platforms.length === 1 ? "" : "s"}
            </Badge>
          </Section.Header>
          <Section.Content>
            {blueprint.platforms.length === 0 ? (
              <p className="bf-p" style={{ padding: "1rem" }}>
                No blueprint has placed resources yet. A Platform gets its
                blueprint when it is registered; open the Platform to add
                resources.
              </p>
            ) : null}
            <BlueprintCanvas
              blueprint={blueprint}
              platforms={platforms}
              selectedId={selectedId}
              onSelect={handleSelect}
            />
          </Section.Content>
        </Section>
      )}

      <Drawer
        header={
          selection?.kind === "company"
            ? "Company"
            : selection?.kind === "platform"
              ? "Platform"
              : selection?.kind === "resource"
                ? "Resource"
                : ""
        }
        isOpen={selection !== null}
        onRequestClose={closeDrawer}
      >
        <BlueprintDrawer
          key={selectedId ?? "none"}
          selection={selection}
          availableTypes={availableTypes}
          busy={busy}
          onAddUsage={handleAddUsage}
          onUpdateUsage={handleUpdateUsage}
          onDeleteUsage={handleDeleteUsage}
          onCreateType={handleCreateType}
          onValidatePlacement={handleValidatePlacement}
        />
      </Drawer>
    </PageLayout>
  );
}
